import React, { useState, useEffect, useMemo } from 'react';
import MatchListing from './MatchListing';
import { TabGroup } from '../common';
import { MATCH_STATUS, type Match, type MatchCounts } from '../../types/match';
import { useNetworkMatches } from '../../hooks/useMatch';

interface NotPursuedMatchesProps {
  onDataChange?: (count: number) => void;
  startDate?: Date | null;
  /** Multi-event: scope to one event; null = all events. */
  eventId?: string | null;
  endDate?: Date | null;
  search: string;
  counts: MatchCounts;
  /** Multi-event: event-aware empty-state copy from the parent. */
  emptyMessage?: string;
}

const NotPursuedMatches = ({
  startDate,
  eventId,
  endDate,
  search,
  counts,
  emptyMessage,
}: NotPursuedMatchesProps) => {
  // Jun-18 (a user V1 test): tooltipKey added so each Passed sub-tab carries an
  // ⓘ explainer (TabGroup renders it), consistent with the Approved sub-tabs.
  const tabs = [
    {
      label: 'All',
      value: 'all',
      tooltipKey: 'matches-subtab-passed-all' as const,
    },
    {
      label: 'Passed by me',
      value: MATCH_STATUS.PASSED_BY_ME,
      // Jun-19 (a user V1 test, live-verify): guard against undefined counts
      // during the date-filter refetch (see ApprovedMatches) — prevents the
      // Matches page white-screen crash.
      count: counts?.passed?.passed_by_me ?? 0,
      tooltipKey: 'matches-subtab-passed-by-me' as const,
    },
    {
      label: 'Passed by other',
      value: MATCH_STATUS.PASSED_BY_OTHER,
      count: counts?.passed?.passed_by_other ?? 0,
      tooltipKey: 'matches-subtab-passed-by-other' as const,
    },
  ];
  const [tab, setTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(21);
  const {
    data: matches,
    isLoading,
    refetch: fetchNetworkMatches,
  } = useNetworkMatches({
    page: currentPage,
    limit,
    status: MATCH_STATUS.REJECTED,
    sub_status: tab,
    start_date: startDate?.toISOString() ?? undefined,
    event_id: eventId ?? undefined,
    end_date: endDate?.toISOString() ?? undefined,
  });

  const filteredMatches = useMemo(() => {
    if (search.length > 2) {
      const searchLower = search.toLowerCase();
      return matches?.result.items.filter((match: Match) => {
        const name = match.other_user?.name ?? '';
        const objective = match.other_user?.objective ?? '';
        return (
          name.toLowerCase().includes(searchLower) ||
          objective.toLowerCase().includes(searchLower)
        );
      });
    }
    return matches?.result.items;
  }, [search, matches]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleTabChange = (tab: string) => {
    setTab(tab);
  };

  useEffect(() => {
    if (endDate && currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchNetworkMatches();
    }
  }, [endDate, currentPage, tab]);

  return (
    <>
      <TabGroup
        className='w-fit bg-white border-2 border-gray-200 whitespace-nowrap overflow-x-auto scrollbar-hide w-full md:w-fit '
        activeTabClassName='!bg-body-800 !border-body-800 hover:!border-body-800 !text-white hover:!shadow-none min-w-[8rem] whitespace-nowrap px-4'
        tabClassName='whitespace-nowrap px-4 min-w-[8rem] w-full'
        tabSize='sm'
        tabs={tabs}
        activeTab={tab}
        onChange={handleTabChange}
      />
      <MatchListing
        matches={filteredMatches}
        currentPage={currentPage}
        totalPages={matches?.result.meta.pages ?? 0}
        totalItems={matches?.result.meta.total ?? 0}
        limit={limit}
        onPageChange={handlePageChange}
        loading={isLoading}
        emptyMessage={emptyMessage}
      />
    </>
  );
};

export default NotPursuedMatches;
